import { routes } from "@/routes/utils";
import { useGetCommunityReview, useGetGlMasterData } from "@/services";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { accountCategoryOptions, subledgerOptions,
  subledgerTypeOptions, } from "@/lib/utils";

const useDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: communityDetail, isLoading: isDetailLoading } = useGetCommunityReview(
    { community_id: id || "", queryParams: { enabled: !!id } }
  );

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: id as string,
  });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "glAccountNumber",
      header: (
        <div className=" w-full">
          Account
          <br /> No.
        </div>
      ),
      accessorFn: (row: any) => String(row.glAccountNumber),
    },
    {
      accessorKey: "accountShortDesc",
      header: (
        <div className=" w-full">
          Account Short <br /> Description
        </div>
      ),
      accessorFn: (row: any) => String(row?.accountShortDesc || ""),
    },
    {
      accessorKey: "accountCategory",
      header: (
        <div className="w-full">
          Account <br /> Category
        </div>
      ),
      accessorFn: (row: any) =>
        row?.accountCategory
          ? accountCategoryOptions.find(
              (val) => val.value == row?.accountCategory
            )?.label
          : "",
    },
    {
      accessorKey: "reconAccount",
      header: (
        <div className=" w-full">
          Recon <br /> Account
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.reconAccount ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "clearingAccount",
      header: (
        <div className=" w-full">
          Clearing <br /> Account
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.clearingAccount ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "noDirectPosting",
      header: (
        <div className=" w-full">
          No Direct <br /> Posting
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.noDirectPosting ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "subledger",
      header: <div className=" w-full">Subledger</div>,
      accessorFn: (row: any) =>
        row?.subledger
          ? subledgerOptions?.find((val) => val.value == row?.subledger)?.label
          : "",
    },
    {
      accessorKey: "subledgerType",
      header: (
        <div className=" w-full">
          Subledger <br /> Type
        </div>
      ),
      accessorFn: (row: any) =>
        row?.subledgerType
          ? subledgerTypeOptions?.find((val) => val.value == row?.subledgerType)
              ?.label
          : "",
    },
  ];

  const comDetails = useMemo(() => {
    const details = communityDetail?.data;
    return {
      communityInfo: {
        title: "Community Info",
        data: [
          { label: "Name", value: details?.name || "" },
          {
            label: "Community Registered Name",
            value: details?.registeredName || "",
          },
          { label: "Language", value: details?.languageCode || "" },
          { label: "Units", value: details?.units || "" },
          { label: "Address 1", value: details?.addressLine1 || "" },
          { label: "Address 2", value: details?.addressLine2 || "" },
          { label: "Zipcode", value: details?.zipcode },
          { label: "State", value: details?.state },
          { label: "City", value: details?.city },
          {
            label: "General Contact Phone",
            value: details?.generalPhoneNo || "",
          },
          {
            label: "Emergency Contact Phone",
            value: details?.emergencyPhoneNo || "",
          },
          {
            label: "Community Manager Name",
            value: details?.communityManagerName || "",
          },
          {
            label: "Property Manager Contact Phone",
            value: details?.propertyManagerPhone || "",
          },
          {
            label: "Contact Email",
            value: details?.contactEmail || "",
          },
          { label: "Website", value: details?.websiteUrl || "" },
        ],
      },
      licenseInfo: {
        title: "License Info",
        data: [
          {
            label: "Community Fed Tax ID",
            value: details?.federalTaxId || "",
          },
          {
            label: "Community State Tax ID",
            value: details?.stateTaxId || "",
          },
          {
            label: "CAM License Number",
            value: details?.camLicenseNumber || "",
          },
          {
            label: "CAM License Start Date",
            value: details?.camLicenseStartDate || "",
          },
          {
            label: "CAM License End Date",
            value: details?.camLicenseEndDate || "",
          },
          { label: "Last AGM Date", value: details?.lastAgmDate || "" },
          {
            label: "Annual Filling Due Date",
            value: details?.annualFilingDueDate || "",
          },
        ],
      },
      finance: {
        title: "Financial Info",
        data: [
          {
            label: "HOA Yearly Due Date",
            value: details?.hoaYearlyDueDate || "",
          },
          {
            label: "HOA Monthly Due Date",
            value: details?.hoaMonthlyDueDate || "",
          },
          {
            label: "HOA Lease Admin Due Date",
            value: details?.hoaLeaseAdminDueDate || "",
          },
          { label: "Currency", value: details?.currency || "" },
          { label: "Bank Name", value: details?.bankName || "" },
          {
            label: "Bank Routing Number",
            value: details?.bankRoutingNumber || "",
          },
          {
            label: "Bank Account Number",
            value: details?.bankAccountNumber || "",
          },
          { label: "Zelle Phone 1", value: details?.zellePhone1 || "" },
          { label: "Zelle Phone 2", value: details?.zellePhone2 || "" },
        ],
      },
      approvals: {
        title: "Approval Info",
        data: [
          {
            label: "AP Invoice Approvals",
            value: details?.apInvoiceApprovals || "",
          },
          {
            label: "Budget Approvals",
            value: details?.budgetApprovals || "",
          },
          {
            label: "Contract Approvals",
            value: details?.contractApprovals || "",
          },
          {
            label: "Work Order Approvals",
            value: details?.workOrderApprovals || "",
          },
        ],
      },
      // announcement: {
      //   title: "Announcement",
      //   data: [
      //     {
      //       label: "Flash News 1",
      //       value: details?.flashNews1 || "",
      //     },
      //     {
      //       label: "Newsletter 1",
      //       value: details?.newsletter1FileName || "",
      //       icon: <img src={PdfIcon} className="h-8" />,
      //     },
      //     {
      //       label: "",
      //       value: "",
      //     },
      //     {
      //       label: "Flash News 2",
      //       value: details?.flashNews2 || "",
      //     },
      //     {
      //       label: "Newsletter 2",
      //       value: details?.newsletter2FileName || "",
      //       icon: <img src={PdfIcon} className="h-8" />,
      //     },
      //   ],
      // },
    };
  }, [communityDetail]);

  const onBackClick = () => {
    navigate(`/${routes.cam}/${routes.communities}`);
  };

  const onEditClick = (step: number) => {
    navigate(`/${routes.cam}/${routes.communities}/${id}/edit-details`, {
      state: { step },
    });
  };

  return {
    onBackClick,
    comDetails,
    isDetailLoading,
    onEditClick,
    columns,
    isLoadingGlMaster,
    glData,
  };
};

export default useDetails;
